<template>
  <div v-if="months.length || openingBalances.length" class="chb">
    <div class="chb__head">
      <span class="chb__title">Anterior a Parkos</span>
      <span class="chb__sub">Meses generados como historial. Corrige los que en realidad no se pagaron.</span>
    </div>

    <div v-for="o in openingBalances" :key="o.id" class="chb__row">
      <span class="chb__month">{{ o.concept }}</span>
      <span class="chb__amount">{{ money(o.amount - o.amountPaid) }} pendiente</span>
    </div>

    <div v-for="m in months" :key="m.id" class="chb__row">
      <span class="chb__month">{{ monthLabel(m.dueDate) }}</span>
      <span class="chb__amount">{{ money(m.amount) }}</span>
      <select class="chb__select" :value="m.status" :disabled="busyId === m.id" :aria-label="`Estado de ${monthLabel(m.dueDate)}`" @change="onStatus(m, $event.target.value)">
        <option value="PAID">Pagado</option>
        <option value="PARTIAL">Pago parcial</option>
        <option value="UNPAID">Sin pagar</option>
      </select>
      <template v-if="partialId === m.id">
        <input v-model.number="partialValue" type="number" min="0" :max="m.amount" class="chb__input" placeholder="Monto pagado" />
        <button type="button" class="chb__btn" :disabled="busyId === m.id" @click="savePartial(m)">Guardar</button>
      </template>
      <span v-else-if="m.status === 'PARTIAL'" class="chb__amount">pagado {{ money(m.amountPaid) }}</span>
    </div>

    <p v-if="error" class="chb__error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getContractHistory, setHistoryMonthPaid } from '@/services/contracts'

const props = defineProps({ contractId: { type: String, required: true } })
const emit = defineEmits(['changed'])

const months = ref([])
const openingBalances = ref([])
const busyId = ref('')
const partialId = ref('')
const partialValue = ref(null)
const error = ref('')

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const monthLabel = (iso) => { const d = new Date(iso); return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` }
const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0)

async function load() {
  try {
    const data = await getContractHistory(props.contractId)
    months.value = data.months || []
    openingBalances.value = data.openingBalances || []
  } catch {
    months.value = []
    openingBalances.value = []
  }
}
watch(() => props.contractId, () => { partialId.value = ''; error.value = ''; load() }, { immediate: true })

async function apply(month, amountPaid) {
  busyId.value = month.id
  error.value = ''
  try {
    await setHistoryMonthPaid(props.contractId, month.id, amountPaid)
    partialId.value = ''
    await load()
    emit('changed')
  } catch (err) {
    error.value = err.message
    await load()
  } finally {
    busyId.value = ''
  }
}

function onStatus(month, value) {
  if (value === 'PAID') return apply(month, month.amount)
  if (value === 'UNPAID') return apply(month, 0)
  partialId.value = month.id
  partialValue.value = month.amountPaid > 0 && month.amountPaid < month.amount ? month.amountPaid : null
}

function savePartial(month) {
  const v = Number(partialValue.value)
  if (!(v > 0 && v < month.amount)) { error.value = 'El pago parcial debe ser mayor a 0 y menor al monto del mes'; return }
  apply(month, v)
}
</script>

<style scoped>
.chb { margin-top: 14px; padding-top: 12px; border-top: 1px dashed #e2e8f0; display: flex; flex-direction: column; gap: 6px; }
.chb__head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 4px; }
.chb__title { font: 600 11px var(--font-body, Inter, sans-serif); text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; }
.chb__sub { font: 400 11px var(--font-body, Inter, sans-serif); color: #94a3b8; }
.chb__row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font: 400 12px var(--font-body, Inter, sans-serif); color: #475569; }
.chb__month { flex: 0 0 76px; }
.chb__amount { color: #94a3b8; }
.chb__select, .chb__input { height: 26px; border: 1px solid #e2e8f0; border-radius: 5px; font: 400 11px var(--font-body, Inter, sans-serif); padding: 0 6px; background: #fff; }
.chb__input { width: 96px; }
.chb__btn { height: 26px; padding: 0 10px; border: none; border-radius: 5px; background: #3b82f6; color: #fff; font: 500 11px var(--font-body, Inter, sans-serif); cursor: pointer; }
.chb__btn:disabled { opacity: 0.5; cursor: default; }
.chb__error { margin: 0; font: 400 11px var(--font-body, Inter, sans-serif); color: #dc2626; }
</style>